import { Truck } from 'lucide-react';
import { Button } from '@/components/ui';
import { useShippingCouriersQuery } from '@/features/shipping/couriers/hooks/useShippingCouriers';

export interface TrackableShipment {
  courierCode?: string | null;
  trackingNo?: string | null;
  /** BE 가 택배사 배송조회 URL 에 송장번호를 넣어 준 링크(QA-510). */
  trackingUrl?: string | null;
}

/**
 * 택배사 조회용 송장번호 — 하이픈·공백을 뺀다(QA-526: CJ대한통운 wblNo=6008-8563-0115 는 "유효하지 않은 운송장번호",
 * 600885630115 는 정상). 송장번호는 숫자·영문이라 그 밖의 글자는 모두 지운다 — 엑셀·이관 데이터의 ‐(U+2010)·–·－(전각)
 * 처럼 하이픈과 비슷한 글자, 공백·탭도 함께(10/26 재이슈).
 */
export const normalizeTrackingNo = (trackingNo: string): string => trackingNo.replace(/[^0-9A-Za-z]/g, '');

/** 배송조회 URL — BE trackingUrl 우선, 없으면 배송 업체 관리의 템플릿({trackingNo}·__INVOICENO__)에 송장번호를 넣는다. */
export const resolveTrackingUrl = (
  info: TrackableShipment | null | undefined,
  couriers: ReadonlyArray<{ courierCode: string; trackingUrlTemplate: string | null }> | undefined,
): string | null => {
  if (!info?.trackingNo) return null;
  const plain = normalizeTrackingNo(info.trackingNo.trim());
  const no = encodeURIComponent(plain);
  if (info.trackingUrl) {
    // BE 링크 안의 송장번호는 화면 값과 표기가 다를 수 있다(화면은 숫자만, 링크는 6009-2835-0415, %2D·공백 인코딩 등 —
    // QA-526 10/26 재이슈). 글자 사이에 구분자가 끼어 있어도 같은 번호면 구분자를 뺀 번호로 바꾼다. 링크의 다른 부분은 그대로.
    if (!plain) return info.trackingUrl;
    const esc = (c: string) => c.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // 글자 사이 구분자 = URL 인코딩(%XX, 예 %2D·%E2%80%90) 또는 숫자·영문이 아닌 글자.
    const pattern = new RegExp(plain.split('').map(esc).join('(?:%[0-9A-Fa-f]{2}|[^0-9A-Za-z%])*'), 'g');
    return info.trackingUrl.replace(pattern, no);
  }
  const template = couriers?.find((c) => c.courierCode === info.courierCode)?.trackingUrlTemplate;
  if (!template) return null;
  return template.replace('{trackingNo}', no).replace('__INVOICENO__', no);
};

/** 고도몰 [배송추적]처럼 작은 팝업 창으로 연다(창이 막히면 새 탭). */
export const openTrackingPopup = (url: string) => {
  const w = window.open(url, 'everline-tracking', 'width=920,height=820,scrollbars=yes,resizable=yes');
  if (!w) window.open(url, '_blank', 'noopener');
};

const TrackingPopupButton = ({ url }: { url: string }) => (
  <Button
    type="button"
    variant="outline"
    size="sm"
    className="h-6 px-2 text-[12px]"
    onClick={(e) => {
      e.stopPropagation();
      openTrackingPopup(url);
    }}
    data-testid="order-tracking-button"
  >
    <Truck size={12} aria-hidden="true" />
    배송추적
  </Button>
);

/** BE 링크가 없을 때만 택배사 목록(배송 업체 관리)을 불러 링크를 만든다. */
const TrackingButtonFromCourier = ({ info }: { info: TrackableShipment }) => {
  const couriers = useShippingCouriersQuery();
  const url = resolveTrackingUrl(info, couriers.data);
  return url ? <TrackingPopupButton url={url} /> : null;
};

/**
 * QA-526 주문 상세 [배송추적] — 송장번호가 있는 주문에서 택배사 배송조회 페이지를 팝업으로 연다.
 * 배송조회 URL 이 없는 택배사(직접 배송 등)면 버튼을 그리지 않는다. 송장번호가 없으면 아무것도 부르지 않는다.
 */
export const TrackingButton = ({ info }: { info: TrackableShipment | null | undefined }) => {
  if (!info?.trackingNo) return null;
  // BE 링크도 resolveTrackingUrl 을 거쳐 송장번호 구분자를 뺀다(QA-526 10/26 — 전엔 BE 링크를 그대로 열어 wblNo 에 하이픈이 남았다).
  const beUrl = info.trackingUrl ? resolveTrackingUrl(info, undefined) : null;
  if (beUrl) return <TrackingPopupButton url={beUrl} />;
  return <TrackingButtonFromCourier info={info} />;
};
